import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { LoadingButton } from '@mui/lab';
import { CircularProgress, Menu, MenuItem, Stack } from '@mui/material';
import { pdf } from '@react-pdf/renderer';
import { PDFDocument, rgb } from 'pdf-lib';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import StaticEnPDF from '../../export/file/GabioDrain Design_Long report- rev.0.pdf';
import StaticItPDF from '../../export/file/GabioDrain Design_Long report- rev.1_ita.pdf';
import Pdf from '../../export/Index';
import Span from '../../shared/component/Span';
import { EReduxState } from '../../store/enum';
import { useAppSelector } from '../../store/hook';
import { selectImage } from '../../store/Image/imageSlice';
import { selectProject } from '../../store/project/projectSlice';
import { selectResult } from '../../store/result/resultSlice';
import { ELanguages, getLanguage } from '../../localization';

interface IExportProps {}

const saveImage = (filename: string, dataUrl: string) => {
    let a = document.createElement('a');
    a.download = filename + '.png';
    a.href = dataUrl;
    a.click();
};

const savePDF = (filename: string, blob: Blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();

    URL.revokeObjectURL(url);
};

const Export = (props: IExportProps) => {
    const { t } = useTranslation();

    const project = useAppSelector(selectProject);
    const result = useAppSelector(selectResult);
    const image = useAppSelector(selectImage);

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [loadingShort, setLoadingShort] = React.useState<boolean>();
    const [loadingLong, setLoadingLong] = React.useState<boolean>();

    const open = Boolean(anchorEl);

    const hasSecondaryDrainageTrench = project.data?.drainageTrenches?.length! > 1;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDownloadImage = () => {
        saveImage(`${project.data?.projectInfo.title} - ${t('preliminaryCalculation')}`, image.data?.preliminaryCalculation!);
        saveImage(`${project.data?.projectInfo.title} - ${t('3DEngine')}`, image.data?.generalView!);
        saveImage(`${project.data?.projectInfo.title} - ${t('slopeTransversalEngine')} - ${t('primaryDrain')}`, image.data?.transversalSectionPrimaryDrainageTrench!);
        if (hasSecondaryDrainageTrench)
            saveImage(`${project.data?.projectInfo.title} - ${t('slopeTransversalEngine')} - ${t('secondaryDrain')}`, image.data?.transversalSectionSecondaryDrainageTrench!);
        saveImage(`${project.data?.projectInfo.title} - ${t('longitudinalStaticView')}`, image.data?.longitudinalStaticSection!);
        saveImage(`${project.data?.projectInfo.title} - ${t('longitudinalSeismicView')}`, image.data?.longitudinalSeismicSection!);
        handleClose();
    };

    const handleDownloadLongReport = async () => {
        setLoadingLong(true);

        const blob = await pdf(<Pdf />).toBlob();
        const dynamicPdfBytes = await blob.arrayBuffer();
        const dynamicPdfDoc = await PDFDocument.load(dynamicPdfBytes);

        let iso = getLanguage();
        const response = await fetch(iso == ELanguages.en ? StaticEnPDF : StaticItPDF);
        const staticPdfBlob = await response.blob();
        const staticPdfBytes = await staticPdfBlob.arrayBuffer();
        const staticPdfDoc = await PDFDocument.load(staticPdfBytes);

        const pages = staticPdfDoc.getPages();
        const { width, height } = pages[0].getSize();
        const font = await staticPdfDoc.embedFont('Helvetica-Bold');
        const fontSize = 20;

        const wrapText = (text: string, font: any, fontSize: number, maxWidth: number): string[] => {
            const words = text.split(' ');
            const lines: string[] = [];
            let currentLine = '';

            for (const word of words) {
                const testLine = currentLine ? `${currentLine} ${word}` : word;
                const testWidth = font.widthOfTextAtSize(testLine, fontSize);
                if (testWidth < maxWidth) {
                    currentLine = testLine;
                } else {
                    if (currentLine) lines.push(currentLine);
                    currentLine = word;
                }
            }

            if (currentLine) lines.push(currentLine);
            return lines;
        };

        const title = project.data!.projectInfo!.title!;
        const maxTextWidth = width - 80;
        const lines = wrapText(title, font, fontSize, maxTextWidth);
        let currentY = height - 190;
        const lineHeight = fontSize + 5;

        lines.forEach(line => {
            const lineWidth = font.widthOfTextAtSize(line, fontSize);
            const x = (width - lineWidth) / 2;
            pages[0].drawText(line, {
                x,
                y: currentY,
                size: fontSize,
                font,
                color: rgb(0.498, 0.498, 0.498),
            });
            currentY -= lineHeight;
        });

        const mergedPdfDoc = await PDFDocument.create();

        const staticPages = await mergedPdfDoc.copyPages(staticPdfDoc, staticPdfDoc.getPageIndices());
        staticPages.forEach(page => mergedPdfDoc.addPage(page));

        const dynamicPages = await mergedPdfDoc.copyPages(dynamicPdfDoc, dynamicPdfDoc.getPageIndices());
        dynamicPages.forEach(page => mergedPdfDoc.addPage(page));

        const mergedPdfBytes = await mergedPdfDoc.save();
        const pdfBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });

        savePDF(`${title}-long-report`, pdfBlob);
        setLoadingLong(false);
        handleClose();
    };

    const handleDownloadShortReport = async () => {
        setLoadingShort(true);
        setTimeout(async () => {
            const pdfBlob = await pdf(<Pdf />).toBlob();
            savePDF(`${project.data!.projectInfo!.title!}-short-report`, pdfBlob);
            setLoadingShort(false);
            handleClose();
        }, 3000);
    };

    return (
        <Stack>
            <LoadingButton
                loading={
                    result.status == EReduxState.fulfilled &&
                    !(
                        image.data?.map &&
                        image.data?.generalView &&
                        image.data?.preliminaryCalculation &&
                        image.data?.transversalSectionPrimaryDrainageTrench &&
                        (hasSecondaryDrainageTrench ? image.data.transversalSectionSecondaryDrainageTrench : true) &&
                        image.data?.longitudinalStaticSection &&
                        image.data?.longitudinalStaticSection &&
                        image.data?.resultCalculation?.axis &&
                        image.data?.resultCalculation?.postFsSeismic &&
                        image.data?.resultCalculation?.postFsStatic &&
                        image.data?.resultCalculation?.preFsSeismic &&
                        image.data?.resultCalculation?.preFsStatic
                    )
                }
                loadingPosition='start'
                variant='outlined'
                startIcon={<DownloadIcon />}
                disabled={
                    !(
                        result.status == EReduxState.fulfilled &&
                        image.data?.map &&
                        image.data?.generalView &&
                        image.data?.preliminaryCalculation &&
                        image.data?.transversalSectionPrimaryDrainageTrench &&
                        (hasSecondaryDrainageTrench ? image.data.transversalSectionSecondaryDrainageTrench : true) &&
                        image.data?.longitudinalStaticSection &&
                        image.data?.longitudinalStaticSection &&
                        image.data?.resultCalculation?.axis &&
                        image.data?.resultCalculation?.postFsSeismic &&
                        image.data?.resultCalculation?.postFsStatic &&
                        image.data?.resultCalculation?.preFsSeismic &&
                        image.data?.resultCalculation?.preFsStatic
                    )
                }
                id='export-button'
                endIcon={<KeyboardArrowDownIcon />}
                aria-controls={open ? 'export-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}>
                <Span text={t('export')} />
            </LoadingButton>
            <Menu
                id='export-menu'
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'export-button',
                }}>
                <MenuItem onClick={handleDownloadShortReport}>
                    <Stack direction={'row'} gap={1}>
                        <span>{t('exportShortPdf')}</span>
                        {loadingShort && <CircularProgress sx={{ margin: '0px!important' }} size={20} />}
                    </Stack>
                </MenuItem>
                <MenuItem onClick={handleDownloadLongReport}>
                    <Stack direction={'row'} gap={1}>
                        <span>{t('exportLongPdf')}</span>
                        {loadingLong && <CircularProgress sx={{ margin: '0px!important' }} size={20} />}
                    </Stack>
                </MenuItem>
                <MenuItem onClick={handleDownloadImage}>{t('exportPng')}</MenuItem>
            </Menu>
        </Stack>
    );
};

export default Export;
